'use client';

import { useMemo, useState } from 'react';
import Image from '@src/components/atoms/safe-image';

// Components
import Button from '@src/components/atoms/button';
import ModalLeads from '@src/components/molecules/modal-leads';
import ModalSuccess from '@src/components/molecules/modal-success';

// Context
import useOnlineProfileCtx from '@src/context/online-profile-ctx';

// Assets
import IWhatsapp2 from 'public/svg/i-whatsapp-2.jsx';
import ICopy from 'public/svg/i-copy.jsx';
import IVerified from 'public/png/verified-icon.png';

import useCopyToClipboard from '@src/hooks/useCopyClipboard';

const ProfileOrganismDesktop = ({ profile, setOpenModal, handleCopy }) => {
  return (
    <div className="flex gap-6 justify-between py-6">
      <div>
        {!!profile?.license_user && (
          <div
            style={{
              background:
                'linear-gradient(90deg, rgba(235, 240, 247, 0.00) 0%, #EBF0F7 100%)',
            }}
            className="p-0.5 flex items-center rounded-[2px] w-fit"
            onClick={() => handleCopy(profile?.license_user)}
          >
            <div className="relative w-[24px] h-[24px] overflow-hidden">
              <Image
                src={IVerified}
                alt="verified"
                width={24}
                height={24}
                className="w-[24px] h-[24px] object-contain"
              />
              <div className="absolute inset-0 -translate-x-full animate-[sweep_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
            </div>
            <p className="flex items-center gap-2 text-[14px] font-satoshi text-[#252526]">
              No. Reg.{' '}
              <span className="text-[14px] font-satoshi-bold text-[#252526] flex items-center gap-1">
                {profile?.license_user} <ICopy />
              </span>
            </p>
          </div>
        )}
        {!!profile?.license_user && (
          <p className="text-[11px] font-satoshi text-[#686A68] italic">
            Tenaga Pemasar Properti Tersertifikasi oleh BNSP
          </p>
        )}

        <p className="text-[52px] font-satoshi-bold text-[#252526] mb-2">
          {profile?.full_name}
        </p>

        <div className="flex items-center mb-2">
          <p className="text-[17px] font-satoshi-bold text-[#252526]">
            {profile?.career_level}
          </p>
          <div className="h-[24px] w-[1px] bg-[#DEDFDE] mx-2" />
          <p className="text-[17px] font-satoshi text-[#686A68]">
            Spesialis {profile?.specialisasi_property} dan Area {profile?.specialisasi_area}
          </p>
        </div>

        {!!profile?.achievement?.length && (
          <div className="py-2 gap-[12px] flex flex-wrap max-w-[777px]">
            {profile?.achievement?.map((item, index) => (
              <div
                key={index}
                className="bg-[#fff] rounded-[4px] px-2 h-[40px] flex items-center gap-1"
              >
                <Image
                  src={item?.icon}
                  alt={item?.name}
                  width={58}
                  height={32}
                  className="h-[32px] aspect-[57.75/32.00] object-contain shadow-[0_0_2px_0_rgba(38,38,39,0.08)]"
                />
                <div>
                  <p className="text-[11px] font-satoshi-bold text-[#252526]">
                    {item?.name}
                  </p>
                  {!!item?.years?.length && (
                    <p className="text-[11px] font-satoshi text-[#686A68]">
                      {item?.years?.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <Button
          onClick={() => setOpenModal('leads')}
          className="!text-[#fff] !bg-[#252526] mt-[40px]"
        >
          <IWhatsapp2 />
          Hubungi Marketing
        </Button>
      </div>

      <div className="w-[324px] h-[324px] rounded-[204px] bg-white flex items-center justify-center dd2-2xl:w-[224px] dd2-2xl:h-[224px]">
        {!!profile?.image_url && (
          <Image
            src={profile?.image_url}
            alt="profile"
            width={1000}
            height={1000}
            className="w-full h-full rounded-[204px] object-cover"
          />
        )}
      </div>
    </div>
  );
};

const ProfileOrganismMobile = ({ profile, setOpenModal, handleCopy }) => {
  return (
    <>
      <div className="max-container">
        <div
          className="flex flex-col justify-center items-center pt-6 px-6 
         dd2-lg:justify-start dd2-lg:items-start"
        >
          <div className="mb-4 w-[200px] h-[200px] rounded-[204px] bg-white flex items-center justify-center">
            {!!profile?.image_url && (
              <Image
                src={profile?.image_url}
                alt="profile"
                width={1000}
                height={1000}
                className="w-full h-full rounded-[204px] object-cover"
              />
            )}
          </div>
          <p className="mb-4 text-[33px] font-satoshi-bold text-[#252526] text-center 
           dd2-lg:text-left">
            {profile?.full_name}
          </p>
          {!!profile?.license_user && (
            <div
              style={{
                background:
                  'linear-gradient(90deg, rgba(235, 240, 247, 0.00) 0%, #EBF0F7 100%)',
              }}
              className="p-0.5 flex items-center rounded-[2px] w-fit"
              onClick={() => handleCopy(profile?.license_user)}
            >
              <div className="relative w-[24px] h-[24px] overflow-hidden">
                <Image
                  src={IVerified}
                  alt="verified"
                  width={24}
                  height={24}
                  className="w-[24px] h-[24px] object-contain"
                />
                <div className="absolute inset-0 -translate-x-full animate-[sweep_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
              </div>
              <p className="flex items-center gap-2 text-[14px] font-satoshi text-[#252526]">
                No. Reg.{' '}
                <span className="text-[14px] font-satoshi-bold text-[#252526] flex items-center gap-1">
                  {profile?.license_user} <ICopy />
                </span>
              </p>
            </div>
          )}

          {!!profile?.license_user && (
            <p className="text-[11px] font-satoshi text-[#686A68] italic">
              Tenaga Pemasar Properti Tersertifikasi oleh BNSP
            </p>
          )}

          <div className="mt-4 flex items-center mb-[12px]">
            <p className="text-[14px] font-satoshi-bold text-[#252526]">
              {profile?.career_level} |{' '}
              <span className="font-satoshi text-[#686A68]">
                Spesialis {profile?.specialisasi_property} dan Area{' '}
                {profile?.specialisasi_area}
              </span>
            </p>
          </div>

          <Button
            onClick={() => setOpenModal('leads')}
            className="!text-[#fff] !bg-[#252526]"
          >
            <IWhatsapp2 />
            Hubungi Marketing
          </Button>
          <div className="my-4 h-[1px] w-full border-b border-dashed border-[#BEAF87]" />
        </div>
      </div>
      {!!profile?.achievement?.length && (
        <div className="py-2 gap-[12px] flex overflow-x-auto scrollbar-hide mb-6 w-screen">
          {profile?.achievement?.map((item, index) => (
            <div
              key={index}
              className="bg-[#fff] rounded-[4px] px-2 h-[40px] flex-none flex items-center gap-1"
            >
              <Image
                src={item?.icon}
                alt={item?.name}
                width={58}
                height={32}
                className="h-[32px] aspect-[57.75/32.00] object-contain shadow-[0_0_2px_0_rgba(38,38,39,0.08)]"
              />
              <div>
                <p className="text-[11px] font-satoshi-bold text-[#252526]">
                  {item?.name}
                </p>
                {!!item?.years?.length && (
                  <p className="text-[11px] font-satoshi text-[#686A68]">
                    {item?.years?.join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default function ProfileOrganism() {
  const { onlineProfile, handleSubmitLeads } = useOnlineProfileCtx();
  const profile = useMemo(() => onlineProfile?.data?.data, [onlineProfile]);
  const [openModal, setOpenModal] = useState('');
  const { handleCopy } = useCopyToClipboard();

  return (
    <>
      <div className="dd2-xl:block hidden">
        <ProfileOrganismMobile
          handleSubmitLeads={handleSubmitLeads}
          profile={profile}
          openModal={openModal}
          setOpenModal={setOpenModal}
          handleCopy={handleCopy}
        />
      </div>
      <div className="dd2-xl:hidden">
        <ProfileOrganismDesktop
          handleSubmitLeads={handleSubmitLeads}
          profile={profile}
          openModal={openModal}
          setOpenModal={setOpenModal}
          handleCopy={handleCopy}
        />
      </div>

      <ModalLeads
        isOpen={openModal === 'leads'}
        onSubmit={(form) =>
          handleSubmitLeads(form, () => setOpenModal('success'))
        }
        onClose={() => setOpenModal('')}
        ctaBtnClassName="!bg-[#252526] !text-[#fff]"
      />

      <ModalSuccess
        isOpen={openModal === 'success'}
        onClose={() => setOpenModal('')}
      />
    </>
  );
}
